import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "quantityInput", "totalDisplay", "rateDisplay", "coinInput", "usdInput", "form",
    "sendingOverlay", "sendingImage", "sendingText", "bancoPromotion",
    "bancoPromotionCopy", "bancoPromotionLink"
  ]

  static values = {
    pigeonImageUrl: String
  }

  connect() {
    console.group("🧪 Exchange Controller Debug")

    this.logTarget("quantityInputTarget", this.quantityInputTarget)
    this.logTarget("totalDisplayTarget", this.totalDisplayTarget)
    this.logTarget("rateDisplayTarget", this.rateDisplayTarget)
    this.logTarget("coinInputTarget", this.coinInputTarget)
    this.logTarget("usdInputTarget", this.usdInputTarget)
    this.logTarget("formTarget", this.formTarget)

    this.exchangeRate = 0
    this.stepSize = 1000
    this.isHolding = false
    this.holdTimeout = null
    this.interval = null
    this.bancoQuoteTimer = null

    this.fetchRate()
    this.isSubmitting = false

    console.groupEnd()
  }

  logTarget(name, target) {
    if (target) {
      console.log(`✅ ${name} found:`, target)
    } else {
      console.warn(`❌ ${name} not found`)
    }
  }

fetchRate() {
  fetch('/exchange_rate')
    .then(res => res.json())
    .then(data => {
      this.usdInRate = Number(data.usd_in_rate) > 0 ? Number(data.usd_in_rate) : 0;
      this.mazaInRate = Number(data.maza_in_rate) > 0 ? Number(data.maza_in_rate) : 0;

      this.updateRateDisplay();
      this.updateTotal();
    })
    .catch(error => console.error("❌ Exchange rate fetch failed", error));
}

updateRateDisplay() {
  const selectedType = this.formTarget.querySelector('input[name="exchange[order_type]"]:checked').value;

  if (selectedType === "USD_IN") {
    this.exchangeRate = this.usdInRate;
  } else if (selectedType === "MAZA_IN") {
    this.exchangeRate = this.mazaInRate;
  }

  this.rateDisplayTarget.textContent = this.exchangeRate > 0 ? Number(this.exchangeRate).toFixed(2) : "N/A";
}

updateTotal() {
  const maza = parseInt(this.quantityInputTarget.value, 10) || 0;
  const selectedType = this.formTarget.querySelector('input[name="exchange[order_type]"]:checked').value;

  let usd;
  if (this.exchangeRate <= 0) {
    usd = 0;
  } else if (selectedType === "USD_IN") {
    usd = maza / this.exchangeRate;
  } else if (selectedType === "MAZA_IN") {
    usd = maza / this.exchangeRate;  // ✅ Corrected: divide for MAZA_IN too
  }

  this.totalDisplayTarget.textContent = `$${usd.toFixed(2)}`;
  this.scheduleBancoQuote(maza, selectedType);
  console.log(`🧮 Updated total: ${maza} MAZA => $${usd.toFixed(2)}`);
}

scheduleBancoQuote(maza, selectedType) {
  if (!this.hasBancoPromotionTarget) return;

  clearTimeout(this.bancoQuoteTimer);
  if (selectedType !== "MAZA_IN") {
    this.bancoPromotionTarget.hidden = true;
    return;
  }

  this.bancoQuoteTimer = setTimeout(() => this.fetchBancoQuote(maza), 250);
}

async fetchBancoQuote(maza) {
  try {
    const response = await fetch(`/banco/quote?maza_amount=${encodeURIComponent(maza)}`, {
      headers: { "Accept": "application/json" }
    });
    if (!response.ok) throw new Error(`Banco quote returned ${response.status}`);

    const data = await response.json();
    if (!data.available) {
      this.bancoPromotionTarget.hidden = true;
      return;
    }

    const bancoCash = Number(data.cash_amount_usd);
    const directCash = Number(maza) / Number(this.mazaInRate);
    const cashDelta = bancoCash - directCash;
    let comparison = "compare with the direct /usd quote";
    if (Number.isFinite(cashDelta)) {
      comparison = Math.abs(cashDelta) < 0.005
        ? "the same cash as the direct /usd quote"
        : `$${Math.abs(cashDelta).toFixed(2)} ${cashDelta > 0 ? "more" : "less"} cash than the direct /usd quote`;
    }

    this.bancoPromotionCopyTarget.textContent =
      `BEST BANCO: ${Number(data.maza_amount).toLocaleString()} MAZA → $${bancoCash.toFixed(2)} cash · ${comparison} · ${data.quoted_rate} MAZA/$1 · $${Number(data.service_fee_usd).toFixed(2)} LP fee`;
    this.bancoPromotionLinkTarget.href = data.url;
    this.bancoPromotionTarget.hidden = false;
  } catch (error) {
    console.warn("Banco promotion unavailable", error);
    this.bancoPromotionTarget.hidden = true;
  }
}


// Add this for order type radio buttons:
orderTypeChanged() {
  this.updateRateDisplay();
  this.updateTotal();
}
  increment() {
    this.adjustQuantity(this.stepSize)
  }

  decrement() {
    this.adjustQuantity(-this.stepSize)
  }

  adjustQuantity(delta) {
    const current = parseInt(this.quantityInputTarget.value, 10) || 0
    const next = Math.min(100000, Math.max(1000, current + delta))
    this.quantityInputTarget.value = next
    this.updateTotal()
  }

async prepareSubmit(event) {
  console.log("🟩 prepareSubmit triggered");
  const maza = parseInt(this.quantityInputTarget.value, 10) || 0;
  const selectedType = this.formTarget.querySelector('input[name="exchange[order_type]"]:checked').value;

  if (!(this.exchangeRate > 0)) {
    event.preventDefault();
    console.error("❌ Exchange rate unavailable");
    alert("Exchange rate unavailable. Please wait for a live rate.");
    return;
  }

  let usd;
  if (selectedType === "USD_IN") {
    usd = maza / this.exchangeRate;
  } else if (selectedType === "MAZA_IN") {
    usd = maza / this.exchangeRate;
  }

  if (!Number.isFinite(usd)) {
    event.preventDefault();
    console.error("❌ Invalid USD total computed", { maza, selectedType, exchangeRate: this.exchangeRate, usd });
    return;
  }

  if (this.isSubmitting) {
    event.preventDefault();
    console.warn("🚫 Duplicate exchange submit blocked");
    return;
  }

  event.preventDefault();

  this.coinInputTarget.value = maza;
  this.usdInputTarget.value = usd.toFixed(2);

  console.log("📤 Submitting form with values:", { maza, usd });
  this.showSendingAnimation("Pigeon in flight...");
  const checkoutButton = event.submitter || this.formTarget.querySelector("#checkout");
  if (checkoutButton) {
    checkoutButton.disabled = true;
    checkoutButton.textContent = "REVIEW TX...";
  }

  this.isSubmitting = true;

  try {
    const response = await fetch(this.formTarget.action, {
      method: (this.formTarget.method || "POST").toUpperCase(),
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
      },
      credentials: "same-origin",
      body: new FormData(this.formTarget)
    });

    if (response.redirected && response.url) {
      window.location.href = response.url;
      return;
    }

    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const data = await response.json();
      if (response.ok && data.redirect_to) {
        window.location.href = data.redirect_to;
        return;
      }

      throw new Error(data.error || data.message || "Exchange review could not be opened.");
    }

    throw new Error(`Unexpected response (${response.status})`);
  } catch (error) {
    console.error("❌ Exchange submit failed", error);
    this.hideSendingAnimation();
    this.isSubmitting = false;
    if (checkoutButton) {
      checkoutButton.disabled = false;
      checkoutButton.textContent = "NEXT >>";
    }
    alert(error.message || "Exchange review could not be opened. Please try again.");
  }
}


  startScroll(event) {
    event.preventDefault()
    if (this.isHolding) return
    this.isHolding = true

    const dir = parseInt(event.currentTarget.dataset.direction, 10)
    if (![1, -1].includes(dir)) {
      this.isHolding = false
      return console.warn("Invalid direction")
    }

    this.adjustQuantity(dir * this.stepSize)

    this.holdTimeout = setTimeout(() => {
      this.interval = setInterval(() => {
        this.adjustQuantity(dir * this.stepSize)
        if (this.stepSize < 5000) this.stepSize += 500
      }, 65)
    }, 220)
  }

  stopScroll() {
    clearTimeout(this.holdTimeout)
    clearInterval(this.interval)
    this.stepSize = 1000
    this.isHolding = false
    this.holdTimeout = null
    this.interval = null
  }

  disconnect() {
    clearTimeout(this.holdTimeout)
    clearInterval(this.interval)
    clearTimeout(this.bancoQuoteTimer)
  }

  showSendingAnimation(message = "Pigeon in flight...") {
    if (!this.hasSendingOverlayTarget || !this.hasSendingImageTarget || !this.hasSendingTextTarget) return
    if (this.hasPigeonImageUrlValue) this.sendingImageTarget.src = this.pigeonImageUrlValue
    this.sendingTextTarget.textContent = message
    this.sendingOverlayTarget.style.display = "flex"
  }

  hideSendingAnimation() {
    if (!this.hasSendingOverlayTarget) return
    this.sendingOverlayTarget.style.display = "none"
  }
}
